<template>
  <button
    type="button"
    :class="['historico-objeto-btn', variant === 'btn' ? 'btn' : `variant-${variant}`]"
    :disabled="!objetoId"
    :title="objetoId ? 'Ver histórico de alterações' : 'Histórico disponível após gravar'"
    v-bind="$attrs"
    @click="showModal = true"
  >
    <i
      class="fa-solid fa-clock-rotate-left"
      :class="{ 'fa-2xl': variant === 'btn' }"
      :style="variant === 'btn' ? 'color: var(--icon)' : ''"
    ></i>
    <span v-if="!iconOnly">{{ label }}</span>
  </button>

  <HistoricoObjetoModal
    v-if="showModal"
    :model-name="modelName"
    :objeto-id="objetoId"
    :titulo="modeloLabel"
    @close="showModal = false"
  />
</template>

<script>
// Botão genérico de histórico: dado um model_name (nome da classe Django,
// ex. "Negocio", "Proposta") e o id de um registo, mostra as alterações
// já registadas na tabela genérica `Historico` (populada pelo
// HistoricoMiddleware) para esse objeto específico. Pode ser colocado em
// qualquer formulário sem precisar de um endpoint/tabela dedicados.
//
// Cada sítio da app tem a sua própria convenção de botões (pill com borda,
// ícone nu, caixa `.btn` com var(--button)...), por isso o visual não vem
// todo fixo: `variant` cobre os casos mais comuns e `class`/`style` passados
// no uso do componente chegam ao <button> (inheritAttrs: false + $attrs) para
// ajustes pontuais sem precisar de mais props.
import HistoricoObjetoModal from "./HistoricoObjetoModal.vue";

export default {
  name: "HistoricoObjetoButton",
  components: { HistoricoObjetoModal },
  inheritAttrs: false,
  props: {
    // Nome exato da classe do modelo Django (case-sensitive) — é este o
    // valor gravado em Historico.model_name pelo middleware.
    modelName: {
      type: String,
      required: true,
    },
    objetoId: {
      type: [Number, String],
      default: null,
    },
    label: {
      type: String,
      default: "Histórico",
    },
    // Mostrar só o ícone, sem o texto do label (útil em barras de ações apertadas)
    iconOnly: {
      type: Boolean,
      default: false,
    },
    // Nome amigável a mostrar no título do modal (ex. "Negócio"). Por
    // omissão usa o próprio modelName.
    modeloLabel: {
      type: String,
      default: "",
    },
    // Aparência base, para encaixar na convenção de botões já usada no sítio
    // onde este componente é colocado:
    // - "pill" (omissão): chip com borda, semelhante ao .action-btn usado
    //   nas páginas de histórico dedicadas.
    // - "bare": só o ícone, sem fundo/borda — para ao lado de outro ícone nu
    //   (ex. o lápis de editar em infoNegocio.vue).
    // - "btn": usa a classe global `.btn` (definida em glass-theme.css /
    //   theme-light.css com !important) — a mesma que os botões "Novo" /
    //   "Atualizar" em tipo.vue já usam, para ficar visualmente idêntico em
    //   vez de tentar reconstruir a cor à mão.
    // Para qualquer outro caso, passa class/style diretamente no uso do
    // componente — chegam ao <button> por baixo.
    variant: {
      type: String,
      default: "pill",
      validator: (v) => ["pill", "bare", "btn"].includes(v),
    },
  },
  data() {
    return {
      showModal: false,
    };
  },
};
</script>

<style scoped>
.historico-objeto-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}

.historico-objeto-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* pill (default) */
.historico-objeto-btn.variant-pill {
  padding: 0.4rem 0.75rem;
  border-radius: 0.5rem;
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text);
  background: var(--fundoForm, rgba(255, 255, 255, 0.05));
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.historico-objeto-btn.variant-pill:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.1);
}

/* bare: ícone nu, sem fundo/borda — ao lado de outros ícones de ação simples */
.historico-objeto-btn.variant-bare {
  background: none;
  border: none;
  padding: 0;
  color: var(--text);
  font-size: 0.875rem;
}

.historico-objeto-btn.variant-bare:hover:not(:disabled) {
  color: #93c5fd;
}

/* variant="btn" renders with the real global `.btn` class — both the
   border/background/color !important overrides in glass-theme.css AND the
   unscoped `.btn { padding: 0.5rem 0.8rem; display: flex; align-items:
   center; font-size: 12px; ... }` rule (see e.g. negocioView.vue's <style>,
   loaded app-wide since it isn't scoped) that gives every `.btn` its real
   padding/layout/font-size. So this variant needs no CSS of its own — the
   `fa-2xl` class on the icon (added in the template) is what siblings use
   too, sized off that inherited 12px, not an invented rem value. */
</style>
